import React, {useEffect, useRef, useState, useCallback} from "react";
import {useLocation, useHistory} from "react-router-dom";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useContext, useContextSelector} from "use-context-selector";
import {SessionContext} from "../context/SessionContext";
import {Form, Card, Modal, Alert, Button} from "react-bootstrap";
import "react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css";
import RangeSlider from "react-bootstrap-range-slider";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faWifi} from "@fortawesome/free-solid-svg-icons/faWifi";
import {faBuilding} from "@fortawesome/free-solid-svg-icons/faBuilding";
import {useTranslation} from "react-i18next";
import {Helmet} from "react-helmet";
import ModalRef from "../util/ModalRef";
import Layout from "../common/Layout";
import useOnFieldChange from "../util/useOnFieldChange";
import {LocalContext} from "../context/LocalContext";
import createUUID from "../util/createUUID";
import FieldContainer from "../common/FieldContainer";
import LayoutContentContainer from "../common/LayoutContentContainer";
import useGetProvider from "../providers/useGetProvider";
import useCreateClientStatusMessage from "../util/useCreateClientStatusMessage";
import useSubmitProviderFeedback from "../routed/useSubmitProviderFeedback";

const AddProviderFeedback = () => {
    const {t} = useTranslation();
    const {executeRecaptcha} = useGoogleReCaptcha();
    const isMounted = useRef(null);
    const [loading, setLoading] = useState(false);
    const [isAlreadySubmittedProviderFeedback, setIsAlreadySubmittedProviderFeedback] = useState(false);
    const clientOnline = useContextSelector(LocalContext, (v) => v[0].clientOnline);
    const modalVisible = useContextSelector(SessionContext, (v) => v[0].modalVisible);
    const getAPIDataProvider = useGetProvider();
    const createClientStatusMessage = useCreateClientStatusMessage();
    const submitProviderFeedback = useSubmitProviderFeedback();
    const [sessionData, setSessionData] = useContext(SessionContext);
    const history = useHistory();

    useEffect(() => {
        isMounted.current = true;
    return () => {
            isMounted.current = false;
        };
    }, []);

    // const [errorsAddProviderFeedback, setErrorsAddProviderFeedback] = useState([]);
    const errorsAddProviderFeedback = useState([])[0];
    const [errorsAddProviderFeedbackFragment, setErrorsAddProviderFeedbackFragment] = useState([]);

    const location = useLocation();

    useEffect(() => {
        if (errorsAddProviderFeedbackFragment.length === 0 && errorsAddProviderFeedback.length > 0) {
            const errors = [];
            for (const error of errorsAddProviderFeedback) {
                errors.push(
                    <p key={createUUID()} className="mb-0">
                        {error}
                    </p>
                );
            }

            setErrorsAddProviderFeedbackFragment([...errors]);
        }
    }, [errorsAddProviderFeedback, errorsAddProviderFeedbackFragment, setErrorsAddProviderFeedbackFragment]);

    const fieldsAddProviderFeedbackInitial = {
        valid: true,
        fields: {
            rating: {
                value: 5,
                validate: true,
                valid: true,
                touched: false,
                required: true,
                rules: [],
                errors: [],
                errorsFragment: [],
            },
            comment: {
                value: "",
                validate: true,
                required: false,
                rules: [
                    {
                        type: "maxLength",
                        length: 200,
                    },
                    {
                        type: "validOnEmpty",
                    },
                    {
                        type: "noRepeatingSpaces",
                    },
                ],
                valid: true,
                touched: false,
                errors: [],
                errorsFragment: [],
            },
        },
    };

    const [fieldsAddProviderFeedback, setFieldsAddProviderFeedback] = useState({...fieldsAddProviderFeedbackInitial});

    const onFieldAddProviderFeedbackChange = useOnFieldChange(fieldsAddProviderFeedback, setFieldsAddProviderFeedback);

    const [APIDataProvider, setAPIDataProvider] = useState(null);
    const [isLoadingAPIDataProvider, setIsLoadingAPIDataProvider] = useState(false);
    const [isLoadedAPIDataProvider, setIsLoadedAPIDataProvider] = useState(false);
    const [isErrorGetAPIDataProvider, setIsErrorGetAPIDataProvider] = useState(false);
    const [isNotFoundAPIDataProvider, setIsNotFoundAPIDataProvider] = useState(false);
    const [isNotValidProviderFeedbackID, setIsNotValidProviderFeedbackID] = useState(false);

    const handleGetAPIDataProvider = useCallback(async () => {
        setIsLoadingAPIDataProvider(true);

        // console.log(location);
        const feedbackIDFull = location.pathname.split("/")[location.pathname.split("/").length - 1];
        const feedbackID = feedbackIDFull.split("--")[0];
        const providerID = feedbackIDFull.split("--")[1];
        // console.log(feedbackIDFull);
        // console.log(feedbackID);
        // console.log(providerID);
        if (!(feedbackID && providerID)) {
            setIsNotValidProviderFeedbackID(true);
        }

        let response;
        try {
            response = await getAPIDataProvider(providerID, feedbackIDFull);
        } catch (error) {
            if (error.status === 404) {
                if (isMounted.current) {
                    setIsNotFoundAPIDataProvider(true);
                    setIsLoadingAPIDataProvider(false);
                    return;
                }
            } else {
                if (isMounted.current) {
                    setIsErrorGetAPIDataProvider(true);
                    createClientStatusMessage(error.createClientStatusMessage, true);
                    setIsLoadingAPIDataProvider(false);
                }
            }
            return;
        }

        if (isMounted.current) {
            setAPIDataProvider(response);
            setIsAlreadySubmittedProviderFeedback(!!response.already_submitted);
            setIsLoadedAPIDataProvider(true);
            setIsLoadingAPIDataProvider(false);
        }
    }, [setIsLoadingAPIDataProvider, getAPIDataProvider, setIsLoadedAPIDataProvider, setIsNotFoundAPIDataProvider, setIsErrorGetAPIDataProvider, createClientStatusMessage, setAPIDataProvider, setIsNotValidProviderFeedbackID, location]);

    const [isLoadingSubmitProviderFeddback, setIsLoadingSubmitProviderFeedback] = useState(false);
    const [isLoadedSubmitProviderFeedback, setIsLoadedSubmitProviderFeedback] = useState(false);
    const [isErrorSubmitProviderFeedback, setIsErrorSubmitProviderFeedback] = useState(false);
    const [isSeenSucccessSubmitProviderFeedback, setIsSeenSuccessSubmitProviderFeedback] = useState(false);

    const handleSubmitProviderFeedback = useCallback(async () => {
        setIsLoadingSubmitProviderFeedback(true);

        const feedbackIDFull = location.pathname.split("/")[location.pathname.split("/").length - 1];

        let recaptchaToken;
        try {
            recaptchaToken = await executeRecaptcha();
        } catch (error) {
            // console.log(error);
            createClientStatusMessage({
                variant: 'danger',
                i18nKey: 'error.external-captcha-error',
                source: `${error.message}`
            }, true);
            setIsLoadingSubmitProviderFeedback(false);
            setIsErrorSubmitProviderFeedback(true);
            return;
        }

        // let response;
        try {
            await submitProviderFeedback(fieldsAddProviderFeedback.fields, feedbackIDFull, recaptchaToken);
        } catch (error) {
            if (isMounted.current) {
                setIsLoadingSubmitProviderFeedback(false);

                if (error?.clientStatusMessage?.i18nKey === 'feedback.already-submitted') {
                    setIsAlreadySubmittedProviderFeedback(true);
                    return;
                }

                setIsErrorSubmitProviderFeedback(true);
                createClientStatusMessage(error.clientStatusMessage, true);
            }
            return;
        }

        if (isMounted.current) {
            setIsLoadedSubmitProviderFeedback(true);
            setIsLoadingSubmitProviderFeedback(false);
        }
    }, [setIsLoadingSubmitProviderFeedback, submitProviderFeedback, fieldsAddProviderFeedback, setIsErrorSubmitProviderFeedback, createClientStatusMessage, isMounted, executeRecaptcha, location]);

    useEffect(() => {
        const loadingData = isLoadingAPIDataProvider || isLoadingSubmitProviderFeddback;

        if (!loading && loadingData) {
            setLoading(true);
        } else if (loading && !loadingData) {
            setLoading(false);
        }
    }, [isLoadingAPIDataProvider, isLoadingSubmitProviderFeddback, loading, setLoading]);

    useEffect(() => {
        if (!isLoadingAPIDataProvider && !isLoadedAPIDataProvider && !isErrorGetAPIDataProvider) {
            handleGetAPIDataProvider();
        }
    }, [isLoadedAPIDataProvider, isLoadingAPIDataProvider, isErrorGetAPIDataProvider, handleGetAPIDataProvider]);

    useEffect(() => {
        if (isNotValidProviderFeedbackID ||
            isNotFoundAPIDataProvider ||
                (isLoadedSubmitProviderFeedback &&
                isSeenSucccessSubmitProviderFeedback)
        ) {
            history.replace('/')
        }
    }, [isNotValidProviderFeedbackID, isNotFoundAPIDataProvider, isLoadedSubmitProviderFeedback, isSeenSucccessSubmitProviderFeedback, history])

    if (isAlreadySubmittedProviderFeedback) {
        return (
            <Layout navSecondaryShow={true} className="bg-light" contentBackgroundArt={true}>
                <LayoutContentContainer style={{maxWidth: "50rem"}}>
                    <Alert variant="warning" className="mt-4 mb-0">{t("AddProviderFeedback.message-already-submitted")}</Alert>
                </LayoutContentContainer>
            </Layout>
        );
    }

    return (
        <Layout navSecondaryShow={true} className="bg-light" contentBackgroundArt={true}>
            <Helmet>
                {isLoadedAPIDataProvider && (
                    <title>{`${t("AddProviderFeedback.page-head-title")}${APIDataProvider.title}`}</title>
                )}
            </Helmet>
            {!clientOnline && !isLoadedAPIDataProvider && (
                <Layout
                    style={{
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div className="p-4 text-muted">
                        <span>
                            <FontAwesomeIcon icon={faWifi} />
                        </span>
                        <span className="ml-2">{t("Providers.message-offline")}</span>
                    </div>
                </Layout>
            )}
            {isLoadedAPIDataProvider && (
                <LayoutContentContainer
                    style={{
                        maxWidth: "50rem",
                    }}
                >
                    <h1 style={{fontWeight: 400, paddingBottom: 0}}>{t("AddProviderFeedback.add-feedback")}</h1>
                    <a
                        style={{
                            display: "flex",
                            alignItems: "center",
                            margin: "2rem 0",
                        }}
                        href={`/provider/${APIDataProvider.id}`}
                        target='_blank'
                        rel="noopener noreferrer"
                    >
                        <div
                            style={{
                                width: "4rem",
                                height: "4rem",
                                position: "relative",
                                borderRadius: "3rem",
                                overflow: "hidden",
                                border: '1px solid #ddd',
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    position: "absolute",
                                    paddingBottom: 0,
                                    paddingTop: 0,
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    overflow: "hidden",
                                    borderRadius: "3rem",
                                    margin: '0.25rem',
                                    background: "#ddd",
                                    boxShadow: "0px 0px 15px 0px rgba(0,0,0,0.075)",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#bbb",
                                }}
                            >
                                {APIDataProvider.logo ? (
                                    <img
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                        alt={`Branding for ${APIDataProvider.title}`}
                                        src={process.env.REACT_APP_API_URL.startsWith("https://") ? APIDataProvider.logo.replace("http://", "https://") : APIDataProvider.logo}
                                    />
                                ) : (
                                        <span
                                            style={{
                                                fontSize: "1rem",
                                                display: "inline-flex",
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faBuilding} />
                                        </span>
                                    )}
                            </div>
                        </div>
                        <div
                            style={{
                                marginLeft: "1.25rem",
                            }}
                        >
                            <span className="h2">{APIDataProvider.title}</span>
                        </div>
                    </a>
                    <Form>
                        <FieldContainer
                            wrap={true}
                            dense={true}
                            label={
                                <Form.Label htmlFor="provider-feedback-rating">
                                    {t("AddProviderFeedback.satisfaction-rating")} {fieldsAddProviderFeedback.fields.rating.required && <span className="form-field-container__required-field-indicator">*</span>}
                                </Form.Label>
                            }
                            control={
                                <>
                                    <Card>
                                        <Card.Body
                                            style={{
                                                maxWidth: "30rem",
                                                margin: "0 auto",
                                                width: "100%",
                                            }}
                                        >
                                            <div
                                                className="p-2 text-center"
                                                style={{
                                                    fontSize: "1.75rem",
                                                    fontWeight: 300,
                                                    fontStyle: "italic",
                                                    flexGrow: 1,
                                                }}
                                            >
                                                {(() => {
                                                    const value = fieldsAddProviderFeedback.fields.rating.value;
                                                    switch (true) {
                                                        case value === 0: {
                                                            return t("AddProviderFeedback.rating.equal-0");
                                                        }
                                                        case value <= 2.5: {
                                                            return t("AddProviderFeedback.rating.less-than-2_5");
                                                        }
                                                        case value <= 3.5: {
                                                            return t("AddProviderFeedback.rating.less-than-3_5");
                                                        }
                                                        case value < 5: {
                                                            return t("AddProviderFeedback.rating.less-than-5");
                                                        }
                                                        case value === 5: {
                                                            return t("AddProviderFeedback.rating.equal-5");
                                                        }
                                                        case value <= 7.5: {
                                                            return t("AddProviderFeedback.rating.less-than-7_5");
                                                        }
                                                        case value < 10: {
                                                            return t("AddProviderFeedback.rating.less-than-10");
                                                        }
                                                        case value === 10: {
                                                            return t("AddProviderFeedback.rating.equal-10");
                                                        }
                                                        default:
                                                            break;
                                                    }
                                                })()}
                                            </div>
                                            <div
                                                style={{
                                                    textAlign: "center",
                                                    fontSize: "1.25rem",
                                                    fontWeight: "300",
                                                }}
                                            >
                                                <strong>{fieldsAddProviderFeedback.fields.rating.value}</strong>
                                                {t("AddProviderFeedback.out-of")}10
                                            </div>
                                            <RangeSlider
                                                id="provider-feedback-rating"
                                                value={Number(fieldsAddProviderFeedback.fields.rating.value)}
                                                min={0}
                                                max={10}
                                                step={1}
                                                onChange={(event) => {
                                                    onFieldAddProviderFeedbackChange(Number(event.target.value), "rating");
                                                }}
                                                disabled={isAlreadySubmittedProviderFeedback}
                                            />
                                        </Card.Body>
                                    </Card>
                                </>
                            }
                        />
                        <FieldContainer
                            wrap={true}
                            dense={true}
                            label={
                                <Form.Label htmlFor="provider-feedback-comment">
                                    {t("AddProviderFeedback.experience-comment")}{" "}
                                    <span className="text-muted">
                                        ({fieldsAddProviderFeedback.fields.comment.value.length} / {fieldsAddProviderFeedback.fields.comment.rules.find((rule) => rule.type === "maxLength").length})
                                    </span>{" "}
                                    {fieldsAddProviderFeedback.fields.comment.required && <span className="form-field-container__required-field-indicator">*</span>}
                                </Form.Label>
                            }
                            control={
                                <Form.Group>
                                    <Form.Control
                                        id="provider-feedback-comment"
                                        as="textarea"
                                        rows="6"
                                        onChange={(event) => {
                                            onFieldAddProviderFeedbackChange(event.target.value, "comment");
                                        }}
                                        isInvalid={!fieldsAddProviderFeedback.fields.comment.valid && fieldsAddProviderFeedback.fields.comment.touched}
                                        isValid={fieldsAddProviderFeedback.fields.comment.valid && fieldsAddProviderFeedback.fields.comment.touched}
                                        disabled={isAlreadySubmittedProviderFeedback}
                                    />
                                    <Form.Control.Feedback type="invalid">{fieldsAddProviderFeedback.fields.comment.errorsFragment}</Form.Control.Feedback>
                                </Form.Group>
                            }
                        />
                        <p style={{fontSize: "0.85rem"}}>
                            <span className="form-field-container__required-field-indicator">*</span>
                            {t("AddProviderFeedback.tip-required-fields")}
                        </p>
                        <p
                            style={{
                                fontSize: "0.85rem",
                                marginTop: "2rem",
                            }}
                        >
                            {t("message-conditions-recaptcha.p1")}
                            <a rel="noopener noreferrer" target="_blank" href="https://policies.google.com/privacy">
                                {t("message-conditions-recaptcha.p2")}
                            </a>
                            {t("message-conditions-recaptcha.p3")}
                            <a rel="noopener noreferrer" target="_blank" href="https://policies.google.com/terms">
                                {t("message-conditions-recaptcha.p4")}
                            </a>{" "}
                            {t("message-conditions-recaptcha.p5")}
                        </p>
                        <div style={{display: "flex", marginTop: "2rem"}}>
                            <Button
                                className="ml-auto"
                                variant="primary"
                                type="submit"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleSubmitProviderFeedback();
                                }}
                                disabled={loading || !fieldsAddProviderFeedback.valid || !clientOnline || isAlreadySubmittedProviderFeedback}
                            >
                                {t("AddProviderFeedback.submit-feedback")}
                            </Button>
                        </div>
                    </Form>
                </LayoutContentContainer>
            )}
            {isErrorSubmitProviderFeedback && (
                <>
                    <Modal
                        container={ModalRef}
                        show={true}
                        onHide={() => {
                            if (modalVisible) {
                                setSessionData({
                                    ...sessionData,
                                    modalVisible: false,
                                });
                            }
                        }}
                        onShow={() => {
                            if (!modalVisible) {
                                setSessionData({
                                    ...sessionData,
                                    modalVisible: true,
                                });
                            }
                        }}
                        centered={true}
                    >
                        <Modal.Body>
                            <Alert variant="danger" className="mb-0">{t("AddProviderFeedback.message-submit-failure")}</Alert>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={() => {setIsErrorSubmitProviderFeedback(false)}}>{t("AddProviderFeedback.close")}</Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
            {isLoadedSubmitProviderFeedback && !isSeenSucccessSubmitProviderFeedback && (
                <>
                    <Modal
                        container={ModalRef}
                        show={true}
                        onHide={() => {
                            if (modalVisible) {
                                setSessionData({
                                    ...sessionData,
                                    modalVisible: false,
                                });
                            }
                        }}
                        onShow={() => {
                            if (!modalVisible) {
                                setSessionData({
                                    ...sessionData,
                                    modalVisible: true,
                                });
                            }
                        }}
                        centered={true}
                    >
                        <Modal.Body>
                            <Alert variant="success" className="mb-0">{t("AddProviderFeedback.message-submit-success")}</Alert>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button onClick={() => {setIsSeenSuccessSubmitProviderFeedback(true)}}>{t("AddProviderFeedback.go-to-home")}</Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </Layout>
    );
};

export default AddProviderFeedback;
